'use client';

import React from 'react';
import {
  Toaster as Sonner,
  ToasterProps,
  toast as sonnerToast,
  ToastT,
} from 'sonner';
import { CheckCircle2, XCircle, Info, AlertTriangle, X } from 'lucide-react';

const getToastStyle = (
  type?: string
): {
  IconComponent: React.ComponentType<{ className?: string }>;
  iconColor: string;
  bgColor: string;
} => {
  switch (type) {
    case 'success': {
      return {
        IconComponent: CheckCircle2,
        iconColor: 'text-success-foreground',
        bgColor: 'bg-success-background',
      };
    }
    case 'error': {
      return {
        IconComponent: XCircle,
        iconColor: 'text-error-foreground',
        bgColor: 'bg-error-background',
      };
    }
    case 'warning': {
      return {
        IconComponent: AlertTriangle,
        iconColor: 'text-warning-foreground',
        bgColor: 'bg-warning-background',
      };
    }
    case 'info':
    default: {
      return {
        IconComponent: Info,
        iconColor: 'text-warning-foreground',
        bgColor: 'bg-warning-background',
      };
    }
  }
};

const Toaster = ({ ...props }: ToasterProps) => (
  <Sonner
    toastOptions={{
      classNames: {
        toast: 'mb-xs w-tile cursor-pointer p-s',
        success: 'bg-success-background',
        error: 'bg-error-background',
        info: 'bg-warning-background',
        warning: 'bg-warning-background',
      },
    }}
    {...props}
  />
);

const CustomToast = ({
  message,
  type = 'info',
  title,
  id,
  onDismiss,
}: {
  message: string;
  type?: 'success' | 'error' | 'info' | 'warning';
  title?: string;
  id: string | number;
  onDismiss?: () => void;
}) => {
  const style = getToastStyle(type);
  const { IconComponent } = style;

  const handleDismiss = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onDismiss) {
      onDismiss();
    } else {
      sonnerToast.dismiss(id);
    }
  };

  return (
    <div
      data-testid="toast-notification"
      className={`mb-xs w-tile cursor-pointer p-s ${style.bgColor}`}
      onClick={handleDismiss}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleDismiss(e);
        }
      }}
    >
      <div className="flex gap-xs">
        <div className={`size-l min-h-l min-w-l ${style.iconColor}`}>
          <IconComponent className="size-full" />
        </div>
        <div className="flex grow flex-col gap-xxxs">
          {title && (
            <div className="font-body text-label-small text-regular">
              {title}
            </div>
          )}
          <div className="font-body text-label-small text-regular">
            {message}
          </div>
        </div>
        <div className="size-m min-h-m min-w-m text-regular">
          <X className="size-full" />
        </div>
      </div>
    </div>
  );
};

interface ToastOptions {
  type?: 'success' | 'error' | 'info' | 'warning';
  title?: string;
  duration?: number;
  position?: ToastT['position'];
}

const toast = (message: string, options?: ToastOptions) => {
  const { type = 'info', title, duration, position } = options || {};
  const toastId = sonnerToast.custom(
    id => <CustomToast message={message} type={type} title={title} id={id} />,
    {
      duration: duration || 4000,
      position,
    }
  );

  return toastId;
};

toast.success = (message: string, options?: Omit<ToastOptions, 'type'>) =>
  toast(message, { ...options, type: 'success' });

toast.error = (message: string, options?: Omit<ToastOptions, 'type'>) =>
  toast(message, { ...options, type: 'error' });

toast.info = (message: string, options?: Omit<ToastOptions, 'type'>) =>
  toast(message, { ...options, type: 'info' });

toast.warning = (message: string, options?: Omit<ToastOptions, 'type'>) =>
  toast(message, { ...options, type: 'warning' });

toast.dismiss = sonnerToast.dismiss;
toast.loading = sonnerToast.loading;

export { Toaster, toast };
